import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSelectedProductPath, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductStateByPath } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import Link from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { AdminCustomSettings } from "@tCooneyBrothers/ApiModels";
import parse, { HTMLReactParserOptions } from "html-react-parser";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface Prop65Styles {
    errorColor?: InjectableCss;
    errorIcon?: InjectableCss;
    warning?: InjectableCss;
    prop65Label?: InjectableCss;
    successModal?: ModalPresentationProps;
    modalContainer?: GridContainerProps;
    messageTextGridItem?: GridItemProps;
    messageText?: TypographyPresentationProps;
    modalMessage?: InjectableCss;
    customPropertyContainer?: InjectableCss;
    prop65Image?: InjectableCss;
    prop65Wrapper?: InjectableCss;
    prop65WarningImage?: InjectableCss;
}

export const prop65Styles: Prop65Styles = {
    errorColor: {
        css: css`
            color: #ef3742;
        `,
    },
    errorIcon: {
        css: css`
            pointer-events: none;
            top: 5px;
            right: 2px;
            width: 24px;
            height: auto;
            padding: 0;
            border-radius: 100%;
            position: relative;
        `,
    },
    warning: {
        css: css`
            // color: #20467b;
            // text-decoration: underline;
            text-transform: uppercase;
            font-size: 13px;
            font-weight: bold;
            > span[class^="TypographyStyle"] {
                color: #20467b;
            }
        `,
    },
    prop65Label: {
        css: css`
            font-size: 13px;
        `,
    },
    successModal: { sizeVariant: "small" },
    messageTextGridItem: { width: 12 },
    modalMessage: {
        css: css`
            font-size: 13px;
        `,
    },
    customPropertyContainer: {
        css: css`
            display: flex;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            flex-direction: column;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    prop65Wrapper: {
        css: css`
            margin-top: 15px;
        `,
    },
    prop65Image: {
        css: css`
            background: url(/UserFiles/Images/Icons/6pt.png) no-repeat 0 0;
            padding: 0px 0 8px 19px;
            color: #193066;
            font-size: 13px;
            font-weight: bold;
            margin-top: 15px;
            background-size: 15px;
        `,
    },
    prop65WarningImage: {
        css: css`
            background: url(/UserFiles/Images/Icons/6pt.png) no-repeat 0 0;
            padding: 4px 0 8px 22px;
            color: #193066;
            font-size: 13px;
            font-weight: bold;
            margin-top: 15px;
            background-size: 22px;
        `,
    },
};

const styles = prop65Styles;
const StyledSpan = getStyledWrapper("span");

interface OwnProps {
    messageToDisplay: string;
}

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const productPath =
        getSelectedProductPath(state) ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    const productState = getProductStateByPath(state, productPath);
    return {
        parentProductState: productState,
        adminCustomSettings: getSettingsCollection(state).adminCustomSettings as AdminCustomSettings,
    };
};

type props = OwnProps & ReturnType<typeof mapStateToProps>;

const Prop65Component: React.FC<props> = ({ messageToDisplay, parentProductState, adminCustomSettings }) => {
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const handleWarning = () => {
        setIsModalOpen(true);
    };

    const modalCloseHandler = () => setIsModalOpen(false);
    const showDefaultProp65WarningMessage = adminCustomSettings.showDefaultProp65WarningMessage;
    return (
        <>
            <StyledWrapper {...styles.customPropertyContainer}>
                {parentProductState.value?.properties["prop65"] || messageToDisplay ? (
                    <StyledWrapper data-test-selector="prop65_wrapper" {...styles.prop65Wrapper}>
                        <StyledSpan {...styles.prop65Image}></StyledSpan>{" "}
                        <StyledSpan {...styles.prop65Label} className="murgan">
                            {" "}
                            {translate("Prop 65: ")}
                        </StyledSpan>
                        <Link {...styles.warning} onClick={handleWarning}>
                            {translate("Warning")}
                        </Link>
                        <Modal
                            headline={translate("Alert")}
                            contentLabel="Alert"
                            {...styles.successModal}
                            isOpen={isModalOpen}
                            handleClose={modalCloseHandler}
                        >
                            <GridContainer {...styles.modalContainer}>
                                <GridItem data-test-selector="prop65Component_wrapper" {...styles.messageTextGridItem}>
                                    <Typography {...styles.messageText}>
                                        <StyledSpan {...styles.prop65WarningImage}></StyledSpan>
                                        <StyledSpan {...styles.warning}>{translate("Warning: ")}</StyledSpan>
                                        <StyledSpan {...styles.modalMessage}>
                                            {showDefaultProp65WarningMessage
                                                ? siteMessage("Default_Prop65_WarningMessage")
                                                : parse(
                                                      parentProductState.value?.properties["prop65"] ||
                                                          messageToDisplay,
                                                      parserOptions,
                                                  )}
                                        </StyledSpan>
                                    </Typography>
                                </GridItem>
                            </GridContainer>
                        </Modal>
                    </StyledWrapper>
                ) : null}
            </StyledWrapper>
        </>
    );
};

export default connect(mapStateToProps)(Prop65Component);
